import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import {
	colorSurface,
	fontPorscheNext,
	fontWeightNormal,
	fontWeightSemibold,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { JSX } from "react"
import type { CarouselImage } from "./ImageCarousel"
import { ImageCarousel } from "./ImageCarousel"

interface ImagesWithDescriptionProps {
	additionalContent?: JSX.Element | string
	description?: string
	footer: {
		text: string
		title: string
	} | null
	images: CarouselImage[]
	title: string
}

export const ImagesWithDescription = ({
	additionalContent,
	description,
	footer,
	images,
	title,
}: ImagesWithDescriptionProps) => {
	return (
		<>
			{images.length > 0 && (
				<ImageCarouselWrapper className="stretch-to-full-modal-width">
					<ImageCarousel images={images} />
				</ImageCarouselWrapper>
			)}

			<TextContent>
				<PText size="md" weight="semibold">
					{title}
				</PText>
				{description && <Description dangerouslySetInnerHTML={{ __html: description }} />}
				{additionalContent}
			</TextContent>

			{images.length > 0 && footer && (
				<>
					<HorizontalRule />
					<TextContent>
						<PText weight="semibold" size="xs">
							{footer.title}
						</PText>
						<FooterText size="xs">{footer.text}</FooterText>
					</TextContent>
				</>
			)}
		</>
	)
}

// NOTE: more margin styles are applied via .stretch-to-full-modal-width class
const ImageCarouselWrapper = styled.figure`
	margin-bottom: ${spacingStaticLg};
`

const TextContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};
	max-width: 36em;
`

const HorizontalRule = styled.hr`
	margin: ${spacingStaticMd} 0;
	border: 0;
	border-top: 1px solid ${colorSurface};
`

const FooterText = styled(PText)`
	margin-top: -${spacingStaticSm};
`

const Description = styled.div`
	font-family: ${fontPorscheNext};
	font-weight: ${fontWeightNormal};

	h1,
	h2,
	h3,
	h4,
	h5,
	h6,
	ul,
	li,
	p {
		padding: 0;
		margin: 0;
		overflow-wrap: break-word;
	}

	p {
		line-height: inherit;
		font-size: inherit;
	}

	li {
		margin-inline-start: ${spacingStaticMd};
		list-style-type: square;
		margin-top: ${spacingStaticSm};
	}

	strong,
	em {
		font-weight: ${fontWeightSemibold};
	}
`
